/**
 * Sanitizer - Enterprise Security & DOMPurify Sanitization Engine
 * Protects against Cross-Site Scripting (XSS), script injection, and attribute-based vulnerabilities.
 * Enforces sanitization across all user inputs and intercepts Element.prototype.innerHTML.
 */

import DOMPurifyModule from 'dompurify';

/**
 * Standard configuration for HTML sanitization in the Crazy Cribbage application.
 * Allows safe layout tags, SVG graphics, styling, and application-specific attributes.
 */
export const DEFAULT_DOMPURIFY_CONFIG = Object.freeze({
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
  ADD_ATTR: [
    'onclick', 'oninput', 'onchange', 'onkeydown', 'autofocus', 'autocomplete',
    'data-opt-key', 'data-brand', 'data-field-type', 'data-seat-id', 'data-hole',
    'data-banter', 'data-count', 'data-theme', 'data-customized', 'data-tab',
    'playsinline', 'fetchpriority', 'aria-label', 'aria-hidden', 'aria-live',
    'aria-modal', 'role', 'tabindex'
  ],
  ADD_TAGS: [
    'circle', 'svg', 'path', 'line', 'rect', 'defs', 'clipPath', 'g', 'polygon',
    'text', 'use', 'style'
  ],
  ALLOW_DATA_ATTR: true
});

/**
 * Configuration for strict user input sanitization (player names, chat, titles, layout names).
 * Completely eliminates any tags and attributes to ensure pure plain-text safety.
 */
export const USER_INPUT_DOMPURIFY_CONFIG = Object.freeze({
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true
});

/**
 * Resolves the active DOMPurify instance across browser and test environments.
 * @returns {Object|null}
 */
export function getDOMPurify() {
  // 1. Browser global DOMPurify
  if (typeof window !== 'undefined' && window.DOMPurify && typeof window.DOMPurify.sanitize === 'function') {
    return window.DOMPurify;
  }

  // 2. Direct module export if it has sanitize
  if (DOMPurifyModule && typeof DOMPurifyModule.sanitize === 'function') {
    return DOMPurifyModule;
  }

  // 3. Factory function instantiated with window
  if (typeof DOMPurifyModule === 'function') {
    const win = typeof window !== 'undefined'
      ? window
      : (typeof globalThis !== 'undefined' && globalThis.window ? globalThis.window : null);
    if (win) {
      try {
        const instance = DOMPurifyModule(win);
        if (instance && typeof instance.sanitize === 'function') {
          return instance;
        }
      } catch (_) {}
    }
  }

  return null;
}

/**
 * Fallback sanitizer for environments where DOMPurify / full DOM is not loaded (e.g. CLI tests).
 * @param {string} str
 * @returns {string}
 */
function fallbackSanitize(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/\bon\w+\s*=\s*(?:'[^']*'|"[^"]*"|[^\s>]+)/gi, '')
    .replace(/javascript\s*:/gi, '');
}

/**
 * Sanitizes HTML content using DOMPurify before setting into DOM elements.
 * Automatically recognizes SVG elements and applies the SVG namespace.
 * @param {string} html
 * @param {Element|null} [contextNode=null]
 * @param {Object} [options={}]
 * @returns {string}
 */
export function sanitizeHtml(html, contextNode = null, options = {}) {
  if (typeof html !== 'string') return html === null || html === undefined ? '' : String(html);
  if (!html.trim()) return '';

  const purify = getDOMPurify();
  if (!purify || typeof purify.sanitize !== 'function') {
    return fallbackSanitize(html);
  }

  const isSvg = contextNode && (
    (typeof SVGElement !== 'undefined' && contextNode instanceof SVGElement) ||
    contextNode.namespaceURI === 'http://www.w3.org/2000/svg'
  );

  const config = {
    ...DEFAULT_DOMPURIFY_CONFIG,
    ...options
  };

  if (isSvg) {
    config.NAMESPACE = 'http://www.w3.org/2000/svg';
  }

  try {
    return purify.sanitize(html, config);
  } catch (_) {
    return fallbackSanitize(html);
  }
}

/**
 * Sanitizes all user inputs (player names, chat messages, room codes, layout names, etc.).
 * Strips all HTML tags, script vectors, and javascript: protocols.
 * @param {*} input
 * @param {Object} [options={}]
 * @returns {string}
 */
export function sanitizeUserInput(input, options = {}) {
  if (input === null || input === undefined) return '';
  const str = String(input);
  if (!str) return '';

  const purify = getDOMPurify();
  let cleaned = '';
  if (purify && typeof purify.sanitize === 'function') {
    try {
      cleaned = purify.sanitize(str, {
        ...USER_INPUT_DOMPURIFY_CONFIG,
        ...options
      });
    } catch (_) {
      cleaned = fallbackSanitize(str).replace(/<[^>]*>/g, '');
    }
  } else {
    cleaned = fallbackSanitize(str).replace(/<[^>]*>/g, '');
  }

  // Strip javascript: pseudo-protocol if present
  cleaned = cleaned.replace(/javascript\s*:/gi, '');

  return cleaned.trim();
}

/**
 * Installs an interceptor on Element.prototype.innerHTML so that all innerHTML assignments
 * across the entire application are automatically sanitized via DOMPurify.
 * @param {Window} [win=window]
 * @returns {boolean} Whether the hook was successfully installed
 */
export function installInnerHtmlHook(win = (typeof window !== 'undefined' ? window : null)) {
  if (!win || !win.Element || !win.Element.prototype) return false;

  const proto = win.Element.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, 'innerHTML')
    || (win.HTMLElement && Object.getOwnPropertyDescriptor(win.HTMLElement.prototype, 'innerHTML'));

  if (!descriptor || !descriptor.set) return false;

  // Prevent double-wrapping
  if (proto.__ccInnerHtmlHookInstalled) return true;

  const originalSet = descriptor.set;
  const originalGet = descriptor.get;

  Object.defineProperty(proto, 'innerHTML', {
    get() {
      return originalGet.call(this);
    },
    set(val) {
      if (typeof val !== 'string') {
        return originalSet.call(this, val === null || val === undefined ? '' : String(val));
      }
      if (!val) {
        return originalSet.call(this, '');
      }
      const sanitized = sanitizeHtml(val, this);
      return originalSet.call(this, sanitized);
    },
    configurable: true,
    enumerable: true
  });

  proto.__ccInnerHtmlHookInstalled = true;

  // Also hook ShadowRoot if available
  if (win.ShadowRoot && win.ShadowRoot.prototype) {
    const srDesc = Object.getOwnPropertyDescriptor(win.ShadowRoot.prototype, 'innerHTML');
    if (srDesc && srDesc.set && !win.ShadowRoot.prototype.__ccInnerHtmlHookInstalled) {
      const origSrSet = srDesc.set;
      const origSrGet = srDesc.get;
      Object.defineProperty(win.ShadowRoot.prototype, 'innerHTML', {
        get() {
          return origSrGet.call(this);
        },
        set(val) {
          if (typeof val !== 'string') {
            return origSrSet.call(this, val === null || val === undefined ? '' : String(val));
          }
          if (!val) return origSrSet.call(this, '');
          const sanitized = sanitizeHtml(val, this);
          return origSrSet.call(this, sanitized);
        },
        configurable: true,
        enumerable: true
      });
      win.ShadowRoot.prototype.__ccInnerHtmlHookInstalled = true;
    }
  }

  return true;
}

/**
 * Installs global event listeners and hooks on user inputs (typing, pasting, changing, prompts).
 * Ensures all user inputs are sanitized with DOMPurify.
 * @param {Window} [win=window]
 * @returns {boolean} Whether hooks were installed
 */
export function installUserInputHook(win = (typeof window !== 'undefined' ? window : null)) {
  if (!win || !win.document) return false;
  if (win.__ccUserInputHookInstalled) return true;

  const doc = win.document;

  // 1. Sanitize on input event when dangerous markup is typed/pasted
  doc.addEventListener('input', (e) => {
    const t = e.target;
    if (!t || (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA')) return;
    if (['checkbox', 'radio', 'file', 'range', 'button', 'submit'].includes(t.type)) return;
    if (typeof t.value === 'string' && (t.value.includes('<') || t.value.includes('>'))) {
      const sanitized = sanitizeUserInput(t.value);
      if (t.value !== sanitized) {
        t.value = sanitized;
      }
    }
  }, true);

  // 2. Sanitize on change event
  doc.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA')) return;
    if (['checkbox', 'radio', 'file', 'range', 'button', 'submit'].includes(t.type)) return;
    if (typeof t.value === 'string') {
      const sanitized = sanitizeUserInput(t.value);
      if (t.value !== sanitized) {
        t.value = sanitized;
      }
    }
  }, true);

  // 3. Sanitize on paste event
  doc.addEventListener('paste', (e) => {
    const t = e.target;
    if (!t || (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA')) return;
    if (['checkbox', 'radio', 'file', 'range', 'button', 'submit'].includes(t.type)) return;
    setTimeout(() => {
      if (typeof t.value === 'string') {
        const sanitized = sanitizeUserInput(t.value);
        if (t.value !== sanitized) {
          t.value = sanitized;
        }
      }
    }, 0);
  }, true);

  // 4. Hook window.prompt to automatically sanitize user inputs returned from prompt()
  if (typeof win.prompt === 'function') {
    const originalPrompt = win.prompt;
    win.prompt = function(...args) {
      const res = originalPrompt.apply(this, args);
      if (typeof res === 'string') {
        return sanitizeUserInput(res);
      }
      return res;
    };
  }

  win.__ccUserInputHookInstalled = true;
  return true;
}

/**
 * Initializes the full Sanitizer suite: innerHTML hook, user input hook, and global exposure.
 * @param {Window} [win=window]
 */
export function initSanitizer(win = (typeof window !== 'undefined' ? window : null)) {
  if (!win) return;
  installInnerHtmlHook(win);
  installUserInputHook(win);

  const purify = getDOMPurify();
  if (purify && !win.DOMPurify) {
    win.DOMPurify = purify;
  }

  win.Sanitizer = Sanitizer;
  win.sanitizeHtml = sanitizeHtml;
  win.sanitizeUserInput = sanitizeUserInput;
}

export const Sanitizer = {
  getDOMPurify,
  sanitizeHtml,
  sanitizeUserInput,
  installInnerHtmlHook,
  installUserInputHook,
  initSanitizer,
  DEFAULT_DOMPURIFY_CONFIG,
  USER_INPUT_DOMPURIFY_CONFIG
};

// Auto-initialize when loaded in a browser context
if (typeof window !== 'undefined') {
  initSanitizer(window);
}

export default Sanitizer;
